import type { GitSidebarCategory } from '@readme/api/src/mappings/sidebar/types';
import type { $TSFixMe } from '@readme/iso';
import type Oas from 'oas';
import type { Operation } from 'oas/operation';

import { HTTP_METHOD } from '@readme/iso';
import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import useClassy from '@core/hooks/useClassy';
import { useProjectStore } from '@core/store';
import getCurrentPageBreadcrumb from '@core/utils/Breadcrumb';

// eslint-disable-next-line no-restricted-imports
import AIDropdown from '@routes/SuperHub/components/AIDropdown';

import Method from '@ui/API/Method';
import Breadcrumb from '@ui/Breadcrumb';
import Button from '@ui/Button';
import CopyToClipboard from '@ui/CopyToClipboard';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import RDMD from '@ui/RDMD';
import Title from '@ui/Title';

import TagTooltip from './components/TagTooltip';
import classes from './style.module.scss';
import './style.scss';

interface APIHeaderProps {
  badgeStyle?: 'classic' | 'modern';
  className?: string;
  doc: {
    deprecated?: boolean;
    excerpt?: string;
    slug: string;
    title: string;
    type?: string;
  };
  isWebhook?: boolean;
  oas?: Oas;
  operation?: Operation;
  servers?: {
    selected?: number;
    variables?: $TSFixMe; // @todo export the `Variables` type in `oas`
  };
  sidebar?: GitSidebarCategory[];
}

/**
 * @param {string} host
 * @param {Operation} operation
 */
function createHeaderUrl(host: string, operation: Operation, isWebhook: boolean, classname: string) {
  const operationId = operation.getOperationId();

  // Split up the string based on the curly brace
  let pathParts: React.ReactNode[] | string[] = operation.path.split(/\{|\}/g);

  if (pathParts.length > 1) {
    const finalPathParts: React.ReactNode[] = [];

    pathParts.forEach((param, index) => {
      // When we split off the curly brace the even elements are the param names
      if (index % 2) {
        finalPathParts.push(
          <label
            key={`path-${operationId}_${param}-${index}`}
            className={classes['APIHeader-url-parameter']}
            htmlFor={`path-${operationId}_${param}`}
          >
            {`{${param}}`}
          </label>,
        );
      } else {
        // And the odd elements are the string literals
        finalPathParts.push(param);
      }
    });

    pathParts = finalPathParts;
  }

  return (
    <span className={classname} data-testid="serverurl" title={`${host}${operation.path}`}>
      {!isWebhook && host}
      {pathParts}
    </span>
  );
}

const APIHeader = React.memo(function APIHeader({
  doc,
  isWebhook,
  oas,
  servers,
  operation,
  badgeStyle = 'classic',
  sidebar,
}: APIHeaderProps) {
  const opts = useMemo(() => ({ copyButtons: true }), []);
  const bem = useClassy(classes, 'headline-container');
  const urlContainerRef = useRef<HTMLDivElement>(null);
  const [hasUrlOverflow, setHasUrlOverflow] = useState(false);
  const [showUrlOverflow, setShowUrlOverflow] = useState(false);

  const breadcrumbTrail = getCurrentPageBreadcrumb(sidebar || [], doc.slug);

  const [isRmdx, showAIDropdown, showBreadcrumbs] = useProjectStore(s => [
    s.data.features.mdx === 'enabled',
    s.data.appearance.ai.dropdown === 'enabled',
    s.data.appearance.navigation.breadcrumbs === 'enabled',
  ]);

  // Check for overflow in the URL container
  useEffect(() => {
    const checkOverflow = () => {
      if (urlContainerRef.current) {
        const container = urlContainerRef.current;
        // Find the actual content element (the span with the URL)
        const contentElement = container.querySelector('[data-testid="serverurl"]') as HTMLElement;

        if (contentElement) {
          const hasHorizontalOverflow = contentElement.scrollWidth > contentElement.clientWidth;
          setHasUrlOverflow(hasHorizontalOverflow);
        }
      }
    };

    // Use setTimeout to ensure DOM is fully rendered
    const timeoutId = setTimeout(checkOverflow, 0);

    // Also check after a short delay to ensure content is laid out
    const delayedCheck = setTimeout(checkOverflow, 100);

    let resizeObserver: ResizeObserver | null = null;
    if (urlContainerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(urlContainerRef.current);
    }

    // Check on resize
    window.addEventListener('resize', checkOverflow);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(delayedCheck);
      window.removeEventListener('resize', checkOverflow);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [oas, servers, operation]);

  // filter operation tags so we only use tags with at least 2 pieces of metadata
  const tags =
    operation?.getTags().filter(tag => {
      return tag.name && (tag.description || tag.externalDocs);
    }) || [];

  return (
    <>
      <header
        className={bem('&', doc.type === 'basic' && '_basic')}
        data-raycast-oas={doc.type === 'endpoint' && (oas?.api?._id as string)}
      >
        {!!showBreadcrumbs && breadcrumbTrail.length > 0 && (
          <Flex align="center" className={bem('-breadcrumbContainer')} gap="size-4" justify="start">
            <Breadcrumb>
              {breadcrumbTrail.map(item =>
                item.slug ? (
                  <Link key={item.slug} to={`${item.slug}`}>
                    {item.title}
                  </Link>
                ) : (
                  <span key={item.title}>{item.title}</span>
                ),
              )}
            </Breadcrumb>
            {tags?.length > 0 && <TagTooltip inline={false} pageId={doc.slug} tags={tags} />}
          </Flex>
        )}

        <div className={bem('-grid-item', '-button-container')}>
          <div id="content-head">
            <Title level={1}>
              {doc.title}
              {!showBreadcrumbs && !!tags?.length && <TagTooltip inline={true} pageId={doc.slug} tags={tags} />}
            </Title>
          </div>
          <AIDropdown className={bem('-ai-dropdown')} doc={doc} operation={operation} showAIDropdown={showAIDropdown} />
        </div>
        {(doc.type === 'endpoint' || doc.type === 'webhook') && !!oas?.api?._id && !!operation && (
          <div className={bem('-article-info')}>
            <Method
              className={bem('-api-method')}
              size="lg"
              style={badgeStyle}
              type={doc.type === 'webhook' ? HTTP_METHOD.WEBHOOK : (operation.method as HTTP_METHOD)}
            />{' '}
            {!!(operation?.isDeprecated() || doc.deprecated) && (
              <Method className={bem('-api-method')} size="lg" style={badgeStyle} type={HTTP_METHOD.DEPRECATED} />
            )}
            <div
              ref={urlContainerRef}
              className={bem('-article-info-url-container', showUrlOverflow && '-article-info-url-container_expanded')}
            >
              {createHeaderUrl(
                oas.url(servers?.selected, {}),
                operation,
                !!isWebhook,
                `${classes['headline-container-article-info-url']}
                ${hasUrlOverflow && !showUrlOverflow && classes['headline-container-article-info-url_overflow']}
                ${showUrlOverflow && classes['headline-container-article-info-url_expanded']}`,
              )}
              {!!hasUrlOverflow && !showUrlOverflow && (
                <Button
                  aria-label="Show full URL"
                  kind="secondary"
                  onClick={() => {
                    setShowUrlOverflow(true);
                  }}
                  size="xs"
                  text
                >
                  <Icon aria-hidden="true" name="more-horizontal" />
                </Button>
              )}
            </div>
            <CopyToClipboard
              buttonSize="xs"
              className={bem('-article-info-button')}
              onCopy={() => {}}
              text={`${oas.url(servers?.selected, {})}${operation.path}`}
            />
          </div>
        )}
        {!!doc.excerpt && (
          <RDMD
            body={doc.excerpt}
            className={bem('grid-item', 'excerpt')}
            opts={opts}
            renderer={isRmdx ? 'rmdx' : 'rdmd'}
          />
        )}
      </header>
    </>
  );
});

export default APIHeader;
